import React, { Fragment, useContext, useState, useEffect, useMemo } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import AppLayout from '../layouts/AppLayout'
import { AppContext } from '../contexts/AppContext'
import { Link } from 'react-router-dom'
import Spinner from 'react-activity/dist/Spinner'
import 'react-activity/dist/Spinner.css'
import { FiEdit } from 'react-icons/fi'
import { RiDeleteBinLine } from 'react-icons/ri'
import { TbSwitchHorizontal } from 'react-icons/tb'
import Tooltip from '../components/Tooltip'
import { RxChevronDown, RxCross2 } from 'react-icons/rx'
import { getTeamImage } from '../utils/team'
import { getCompetitionImage } from '../utils/competition'
import { Listbox } from '@headlessui/react'

const AllTeams = () => {
  const { getteams, switchteam, userToken, isLoading, getCompetitions, deleteTeam } = useContext(AppContext)

  const [competitions, setCompetitions] = useState([])
  const [sLoading, setsLoading] = useState(false)
  const [toSwitch, setToSwitch] = useState('')
  const [modalopen, setModalopen] = useState(false)
  const [steam, setsTeam] = useState('')
  const [teamToSwitch, setTeamToSwitch] = useState(null)
  const [switchFromCompetition, setSwitchFromCompetition] = useState('')
  const [switchToCompetition, setSwitchToCompetition] = useState('')

  const switchToCompetitions = useMemo(() => {
    return competitions.filter((comp) => comp.id !== switchFromCompetition.id)
  }, [competitions, switchFromCompetition])

  const fetchCompetitionsWithTeams = async () => {
    const comps = await getCompetitions({ includeTeams: true })
    setCompetitions(comps)
  }

  const switchTeam = async () => {
    setsLoading(true)
    let response = await switchteam(toSwitch)
    setsLoading(false)
    if (response) {
      setModalopen(false)
      setTeamToSwitch(null)
      fetchCompetitionsWithTeams()
    }
  }

  const handleTeamSwitch = async (teamToSwitchParam, switchFromCompetitionName, teamToSwitchId) => {
    setToSwitch(teamToSwitchId)
    setTeamToSwitch(teamToSwitchParam)
    setSwitchFromCompetition(switchFromCompetitionName)
    setModalopen(true)
  }

  const closeModal = () => {
    setToSwitch('')
    setTeamToSwitch('')
    setSwitchFromCompetition('')
    setSwitchToCompetition('')
    setModalopen(false)
    setsTeam('')
  }

  useEffect(() => {
    fetchCompetitionsWithTeams()
  }, [])

  return (
    <AppLayout pageName='All Teams'>
      {isLoading ? (
        <section>
          <div className='flex justify-center w-full py-12'>
            <Spinner
              color='#CCCCCC'
              size={24}
            />
          </div>
        </section>
      ) : (
        <>
          <Transition.Root
            show={modalopen}
            as={Fragment}
          >
            <Dialog
              as='div'
              className='relative z-50'
              onClose={closeModal}
            >
              <Transition.Child
                as={Fragment}
                enter='ease-out duration-300'
                enterFrom='opacity-0'
                enterTo='opacity-100'
                leave='ease-in duration-200'
                leaveFrom='opacity-100'
                leaveTo='opacity-0'
              >
                <div className='fixed inset-0 hidden bg-gray-50 bg-opacity-75 blur-xl transition-opacity md:block' />
              </Transition.Child>

              <div className='fixed inset-0 z-50 overflow-y-auto'>
                <div className='flex min-h-full items-stretch justify-center text-center md:items-center md:px-2 lg:px-4'>
                  <Transition.Child
                    as={Fragment}
                    enter='ease-out duration-300'
                    enterFrom='opacity-0 translate-y-4 md:translate-y-0 md:scale-95'
                    enterTo='opacity-100 translate-y-0 md:scale-100'
                    leave='ease-in duration-200'
                    leaveFrom='opacity-100 translate-y-0 md:scale-100'
                    leaveTo='opacity-0 translate-y-4 md:translate-y-0 md:scale-95'
                  >
                    <Dialog.Panel className='flex w-full transform text-left text-base transition md:my-8 md:max-w-lg md:px-4 lg:max-w-lg'>
                      <div className='relative w-full items-center overflow-hidden bg-white border border-main rounded-lg text-neutral-800 shadow-2xl p-8'>
                        <div className='w-full space-y-2'>
                          <h5 className='text-xl font-medium'>Switch Team</h5>
                          <div
                            className={`p-3 rounded-lg bg-neutral-50 hover:bg-neutral-100 border transition-colors relative cursor-pointer`}
                          >
                            <div className='flex flex-row space-x-4 items-center'>
                              {teamToSwitch && (
                                <img
                                  className='w-12 h-12 rounded-md'
                                  src={getTeamImage(teamToSwitch?.logo, teamToSwitch?.name)}
                                  alt={teamToSwitch?.name?.toLowerCase().replaceAll(' ', '')}
                                />
                              )}
                              <div>
                                <div className='text-neutral-500 font-medium tracking-wide uppercase text-xs'>
                                  {switchFromCompetition.name}
                                </div>
                                <div className='heading text-xl tracking-wide font-semibold text-black'>
                                  {teamToSwitch?.name}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className={`p-3 rounded-lg bg-neutral-50 border transition-colors relative`}>
                            <Listbox
                              value={switchToCompetition}
                              onChange={setSwitchToCompetition}
                            >
                              <Listbox.Label>Competition</Listbox.Label>
                              <div className='relative mt-1'>
                                <Listbox.Button className='form-control relative w-full cursor-default rounded-lg bg-white py-4 pl-3 pr-10 text-left focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 sm:text-sm cursor-pointer'>
                                  <span className='block truncate'>{switchToCompetition.name}</span>
                                  <span className='pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2'>
                                    <RxChevronDown
                                      className='h-5 w-5 text-gray-400'
                                      aria-hidden='true'
                                    />
                                  </span>
                                </Listbox.Button>
                                <Transition
                                  as={Fragment}
                                  leave='transition ease-in duration-100'
                                  leaveFrom='opacity-100'
                                  leaveTo='opacity-0'
                                >
                                  <Listbox.Options className='absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm z-10'>
                                    {switchToCompetitions.map((comp) => (
                                      <Listbox.Option
                                        key={comp.id}
                                        className={({ active }) =>
                                          `relative cursor-default select-none py-2 px-4 ${
                                            active ? 'bg-amber-100 text-amber-900' : 'text-gray-900'
                                          }`
                                        }
                                        value={comp}
                                      >
                                        <div className='flex justify-start gap-4 text-base font-medium items-center text-neutral-800'>
                                          {comp.logo && (
                                            <img
                                              width='30'
                                              height='30'
                                              src={getCompetitionImage(comp.logo)}
                                              alt={comp.name}
                                              className='rounded border-2 border-neutral-100'
                                            />
                                          )}
                                          {comp.name}
                                        </div>
                                      </Listbox.Option>
                                    ))}
                                  </Listbox.Options>
                                </Transition>
                              </div>
                            </Listbox>
                          </div>
                          <button
                            type='button'
                            className='absolute top-3 right-3 text-gray-400 hover:text-gray-500'
                            onClick={() => closeModal()}
                          >
                            <span className='sr-only'>Close</span>
                            <RxCross2
                              className='h-5 w-5'
                              aria-hidden='true'
                            />
                          </button>
                        </div>
                        <div className='pt-7 mb-3 flex justify-end space-x-2'>
                          <button
                            disabled={sLoading}
                            className='btn-cancel'
                            onClick={() => closeModal()}
                          >
                            No, Cancel
                          </button>
                          <button
                            disabled={sLoading}
                            className='btn-submit !px-6'
                            onClick={() => switchTeam()}
                          >
                            {sLoading === false ? 'Yes, Switch' : <Spinner animating={sLoading} />}
                          </button>
                        </div>
                      </div>
                    </Dialog.Panel>
                  </Transition.Child>
                </div>
              </div>
            </Dialog>
          </Transition.Root>
          {competitions.map((comp) => {
            return (
              <section
                className='teams'
                key={comp.id}
              >
                <div className='flex sm:flex-row flex-col justify-between items-start'>
                  <div className='w-full md:w-1/2 mb-4'>
                    <img
                      height='auto'
                      width='200px'
                      className='rounded-lg'
                      alt={comp.name}
                      src={getCompetitionImage(comp.logo)}
                    />
                  </div>
                  {userToken.type == 'Admin' && (
                    <Link to={'/teams/create'}>
                      <button className='btn-main mb-2'>Create Team</button>
                    </Link>
                  )}
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  {comp.teams.map((team, index) => {
                    return (
                      <div
                        key={index}
                        className='relative'
                      >
                        <Link to={`/teams/${team.name.toLowerCase().replaceAll(' ', '')}/${team.id}`}>
                          <div
                            className={`p-4 hover:bg-neutral-50 border-2 rounded-lg border-neutral-100 cursor-pointer relative`}
                          >
                            <div className='flex space-x-2 text-base font-medium items-center text-neutral-800'>
                              <img
                                width='30'
                                height='30'
                                src={getTeamImage(team?.logo, team?.name)}
                                alt={team.name.toLowerCase().replaceAll(' ', '')}
                              />
                              <span>{team.name}</span>
                            </div>
                          </div>
                        </Link>
                        {userToken.type == 'Admin' && (
                          <div className='absolute right-0 top-0 h-full grid grid-rows-3'>
                            <button
                              className='flex-1 p-1 bg-white border'
                              onClick={() => handleTeamSwitch(team, comp, team.id)}
                            >
                              <Tooltip
                                text='switch'
                                customClass='!bg-neutral-400'
                              >
                                <TbSwitchHorizontal className='text-neutral-500 text-xs' />
                              </Tooltip>
                            </button>
                            <Link
                              className='flex-1 p-1 bg-white border'
                              to={`/teams/edit/${team.id}`}
                            >
                              <Tooltip
                                text='edit'
                                customClass='!bg-blue-400'
                              >
                                <FiEdit className='text-blue-500 text-xs' />
                              </Tooltip>
                            </Link>
                            <button
                              className='flex-1 p-1 bg-white border rounded-br'
                              onClick={async () => {
                                const res = await deleteTeam(team.id)
                                if (res === 'success') {
                                  fetchCompetitionsWithTeams()
                                }
                              }}
                            >
                              <Tooltip
                                text='delete'
                                customClass='!bg-red-400'
                              >
                                <RiDeleteBinLine className='text-red-500 text-xs' />
                              </Tooltip>
                            </button>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </section>
            )
          })}
          {/* <section className='teams'>
            <div className='flex sm:flex-row flex-col justify-between items-start'>
              <div className='w-full md:w-1/2 mb-4'>
                <img
                  height='100'
                  className='rounded-lg'
                  alt='Ligue 2'
                  src={`/images/teams/ligue2.png`}
                />
              </div>
              <Link to={'/teams/create'}>
                <button className='btn-main mb-2'>Create Team</button>
              </Link>
            </div>
            <div className='grid grid-cols-2 gap-4'>
              {ligue2.map((team, index) => {
                return (
                  <div
                    key={index}
                    className='relative'
                  >
                    <Link to={`/teams/${team.name.toLowerCase().replaceAll(' ', '')}/${team.id}`}>
                      <div
                        className={`p-4 hover:bg-neutral-50 border-2 rounded-lg border-neutral-100 cursor-pointer relative`}
                      >
                        <div className='flex space-x-2 text-base font-medium items-center text-neutral-800'>
                          <img
                            width='30'
                            height='30'
                            src={getTeamImage(team?.logo, team?.name)}
                            alt={team.name.toLowerCase().replaceAll(' ', '')}
                          />{' '}
                          <span>{team.name}</span>
                        </div>
                      </div>
                    </Link>
                    {userToken.type == 'Admin' && (
                      <div className='absolute right-0 top-0 h-full grid grid-rows-3 z-10'>
                        <button
                          className='flex-1 p-1 bg-white border'
                          onClick={() => handleTeamSwitch(team, 'Ligue 2', team.id)}
                        >
                          <Tooltip
                            text='switch'
                            customClass='!bg-neutral-400'
                          >
                            <TbSwitchHorizontal className='text-neutral-500 text-xs' />
                          </Tooltip>
                        </button>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </section> */}
        </>
      )}
    </AppLayout>
  )
}

export default AllTeams
